import React from 'react';
import { Eye, Target, HeartPulse, ShieldCheck, UserCheck, ChevronRight } from 'lucide-react';
import { useCMS } from '../context/CMSContext';

const About = () => {
  const { content, resolveImage, getPageData } = useCMS();
  const pageData = getPageData('/about');

  const c = (key, fallback) => {
    if (pageData && pageData.sectionData) {
      // Logic: if key is 'about_who_title', check 'who' section for 'title'
      const sections = ['header', 'who', 'vision', 'mission', 'values', 'sdg'];
      for (const type of sections) {
        if (key.includes(`_${type}_`)) {
          const field = key.split(`_${type}_`)[1];
          const val = pageData.sectionData[type]?.[field];
          if (val) return val;
        }
      }
    }
    return content[key] || fallback;
  };

  const SdgHorizontalScroll = () => {
 const sdgCards = [
    { id: '1', color: '#E5243B', image: '/images/db1a8ef7d8734931ae932f81090d708f.jpg.jpeg', alt: 'No Poverty' },
    { id: '2', color: '#DDA63A', image: '/images/SDG 2.jpg.jpeg', alt: 'Zero Hunger' },
    { id: '3', color: '#4C9F38', image: '/images/SDG 3.jpg.jpeg', alt: 'Good Health' },
    { id: '4', color: '#C5192D', image: '/images/SDG4.jpg.jpeg', alt: 'Quality Education' },
    { id: '5', color: '#ED1944', image: '/images/SDG 5.jpg.jpeg', alt: 'Gender Equality' },
    { id: '8', color: '#A21942', image: '/images/SDG 8.jpg.jpeg', alt: 'Decent Work' },
    { id: '11', color: '#FD9D24', image: '/images/SDG 11.jpg.jpeg', alt: 'Sustainable Cities' },
    { id: '17', color: '#19486A', image: '/images/SDG.jpg.jpeg', alt: 'Partnerships' }
  ];

    return (
    <div className="w-full bg-black py-10 px-6">
      {/* Horizontal scrolling track container */}
      <div className="flex gap-4 overflow-x-auto pb-6 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-black snap-x snap-mandatory">
        {sdgCards.map((card) => (
          <div
            key={card.id}
            // p-2 creates that exact "small space" showing the background color around the image
            className="w-[180px] h-[180px] sm:w-[200px] sm:h-[200px] flex-shrink-0 snap-start relative rounded-[1.5rem] overflow-hidden shadow-2xl transition-transform duration-300 hover:scale-[1.03] bg-white p-2"
            style={{ backgroundColor: card.color }}
          >
            {/* The Image expands completely to fill the shape up to the padding limit */}
            <img 
              src={card.image} 
              alt={card.alt} 
              className="w-full h-full object-cover rounded-[1.1rem]" 
            />
          </div>
        ))}
      </div>
    </div>
  );
}

  return (
    <div className="animate-in fade-in duration-700">
      
      {/* Header Section */}
      <section className="pt-6 pb-24 bg-brandWhite border-b border-brandBeige">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[3rem] overflow-hidden shadow-2xl -mt-10 sm:-mt-20">
            <div className="w-full aspect-[16/9] md:aspect-[21/9] bg-black overflow-hidden">
              <video
                className="w-full h-full object-cover"
                src="/videos/WhatsApp%20Video%202026-06-20%20at%2015.09.37.mp4"
                controls
                autoPlay
                muted
                loop
                playsInline
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 bg-brandBeige">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img className="rounded-[3rem] shadow-2xl w-full h-auto object-cover max-h-[450px] lg:max-h-none" alt="Who we are" src={resolveImage('about_who_image', '/images/WhatsApp Image 2026-06-20 at 16.17.09.jpeg')} />
              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-brandGreen/20 rounded-full blur-3xl"></div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold mb-6">Who We Are</h2>
              <p className="text-brandBlack/70 mb-6 leading-relaxed">
                Eunikare International is a community impact organization in Uganda committed to transforming lives through education, economic empowerment, and sustainable development. We work with children, youth, women, and communities to create opportunities and strengthen livelihoods.
              </p>
              <p className="text-brandBlack/70 mb-8 leading-relaxed">
                Started over 10 years ago as an orphanage supporting 69 children, we evolved to address root causes affecting families and communities.
              </p>
              <p className="text-brandBlack/70 mb-8 leading-relaxed">
                We empower youth and women to strengthen families, reduce child vulnerability, and promote long term social and economic transformation across communities and contribute to the Global Sustainable Development Goals.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-brandWhite p-6 rounded-2xl shadow-sm border border-brandGreen/10">
                  <Eye className="w-10 h-10 text-brandGreen mb-4" />
                  <h4 className="font-bold text-lg mb-2">{c('about_vision_title', 'Vision')}</h4>
                  <p className="text-xs text-brandBlack/60">{c('about_vision_text', 'A world where empowered and self reliant communities drive inclusive economic growth and sustainable development.')}</p>
                </div>
                
                <div className="bg-brandWhite p-6 rounded-2xl shadow-sm border border-brandMauve/10">
                  <Target className="w-10 h-10 text-brandMauve mb-4" />
                  <h4 className="font-bold text-lg mb-2">{c('about_mission_title', 'Mission')}</h4>
                  <p className="text-xs text-brandBlack/60">{c('about_mission_text', 'To unlock potential and transform lives by advancing education, economic empowerment, and community development through innovative programs, strategic partnerships, and sustainable opportunities.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Core Values */}
      <section className="py-24 bg-brandWhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">{c('about_values_title', 'Our Core Values')}</h2>
            <div className="h-1 w-16 bg-brandMauve mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-brandBeige p-10 rounded-[2.5rem] hover:bg-brandGreen hover:text-brandWhite transition-all duration-500 group shadow-sm">
              <HeartPulse className="w-12 h-12 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-4">{c('about_values_value1_title', 'Empowerment')}</h3>
              <p className="opacity-80 leading-relaxed">{c('about_values_value1_desc', 'We equip individuals with knowledge and resources to drive sustainable growth.')}</p>
            </div>
            
            <div className="bg-brandBeige p-10 rounded-[2.5rem] hover:bg-brandGreen hover:text-brandWhite transition-all duration-500 group shadow-sm">
              <ShieldCheck className="w-12 h-12 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-4">{c('about_values_value2_title', 'Integrity')}</h3>
              <p className="opacity-80 leading-relaxed">{c('about_values_value2_desc', 'We uphold transparency, accountability, and ethical practices in all actions.')}</p>
            </div>
            
            <div className="bg-brandBeige p-10 rounded-[2.5rem] hover:bg-brandGreen hover:text-brandWhite transition-all duration-500 group shadow-sm">
              <UserCheck className="w-12 h-12 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-4">{c('about_values_value3_title', 'Inclusivity')}</h3>
              <p className="opacity-80 leading-relaxed">{c('about_values_value3_desc', 'We ensure that no one is left behind, designing programs that embrace diversity.')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainable Development Goals */}
      <section className="py-24 bg-brandBlack text-brandWhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">{c('about_sdg_title', 'Our Work Contributes to Global Goals')}</h2>
            <p className="text-brandWhite/60 max-w-2xl mx-auto">
              {c('about_sdg_desc', 'Our initiatives align with the UN Sustainable Development Goals, ensuring every program drives measurable impact and supports community transformation.')}
            </p>
          </div>

          <SdgHorizontalScroll />

          <div className="mt-20 text-center">
            <p className="text-brandWhite/60 italic mb-8">“See how each of our initiatives drives these global goals in action”</p>
            <a href="/#/initiatives" className="inline-flex items-center bg-brandGreen text-brandWhite px-8 py-3 rounded-full font-bold hover:scale-105 transition-transform">
              View Our Initiatives <ChevronRight className="ml-1 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default About;